import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { GraduationCap, Briefcase, Building2, HeartHandshake, Baby, ArrowRight, Sparkles } from 'lucide-react';
import { PremiumButton, FlowerMotif } from './premium/Decor';
import MaroonAmbientBackdrop from './premium/MaroonAmbientBackdrop';

const PERSONAS = [
  {
    icon: GraduationCap,
    hi: 'विद्यार्थी', en: 'Students',
    pHi: 'जो अपनी शक्ति, सही शिक्षा-दिशा और भविष्य के करियर पर स्पष्टता चाहते हैं।',
    pEn: 'Who want clarity on their strengths, academic path, and future career direction.',
  },
  {
    icon: Briefcase,
    hi: 'नौकरी ढूँढ रहे लोग', en: 'Job Seekers',
    pHi: 'जो सही अवसर पाने या प्रतियोगी परीक्षाओं में सफलता के लिए संघर्ष कर रहे हैं।',
    pEn: 'Who are struggling to find the right opportunity or succeed in competitive exams.',
  },
  {
    icon: Building2,
    hi: 'व्यापारी', en: 'Business Owners',
    pHi: 'जो नुकसान, अनिश्चित निर्णयों, या निर्णय लेने के सही समय की उलझन का सामना कर रहे हैं।',
    pEn: 'Who are facing losses, uncertain decisions, or confusion about the right timing to act.',
  },
  {
    icon: HeartHandshake,
    hi: 'युगल / रिश्ते', en: 'Couples',
    pHi: 'जो रिश्तों की चुनौतियों या विवाह में देरी का सामना कर रहे हैं।',
    pEn: 'Who are dealing with relationship challenges or delays in marriage.',
  },
  {
    icon: Baby,
    hi: 'अभिभावक', en: 'Parents',
    pHi: 'जो अपने बच्चे के भविष्य, शक्तियों और सही दिशा पर मार्गदर्शन चाहते हैं।',
    pEn: 'Who want clarity on their child\'s future, strengths, and right direction.',
  },
];

const WhoShouldGetSection = ({ onOpenForm }) => {
  const { language } = useLanguage();
  const isHi = language === 'hi';

  return (
    <section
      data-testid="who-should-get-section"
      className="relative py-16 md:py-24 overflow-hidden text-brand-gold-light"
      style={{
        background:
          'linear-gradient(180deg, #4A0912 0%, #73111E 50%, #4A0912 100%)',
      }}
    >
      <MaroonAmbientBackdrop variant="section" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Custom dark-themed section header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="w-12 h-px bg-brand-gold/60" />
            <FlowerMotif className="w-4 h-4 text-brand-gold animate-pulse-soft" />
            <span className="w-12 h-px bg-brand-gold/60" />
          </div>
          <p className={`text-brand-gold uppercase tracking-[0.3em] text-xs sm:text-sm font-semibold mb-3 ${isHi ? 'font-hindi' : ''}`}>
            {isHi ? 'आपके लिए' : 'For You'}
          </p>
          <h2 className={`font-playfair font-bold text-3xl md:text-4xl lg:text-5xl text-brand-gold-light leading-tight mb-4 ${isHi ? 'font-hindi-serif' : ''}`}>
            {isHi ? 'किसे जन्म कुंडली बनवानी चाहिए?' : 'Who Should Get a Janam Kundli Made?'}
          </h2>
          <p className={`text-white/75 text-base md:text-lg leading-relaxed ${isHi ? 'font-hindi' : 'font-outfit'}`}>
            {isHi
              ? 'जो भी जीवन की किसी दुविधा में हैं — आपकी कुंडली अगले कदम का मार्गदर्शन कर सकती है। यह आपको सही समय समझने, बेहतर निर्णय लेने और स्पष्टता के साथ आगे बढ़ने में मदद करती है।'
              : 'Anyone facing a dilemma in life — let your Kundli guide your next steps. It helps you understand the right timing, make better decisions, and move forward with clarity and confidence.'}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {PERSONAS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                className="group relative bg-white/5 backdrop-blur-md border-2 border-brand-gold/25 rounded-3xl p-7 md:p-8 hover:border-brand-gold hover:bg-white/10 hover:-translate-y-2 transition-all duration-500 opacity-0 animate-fade-up"
                style={{ animationDelay: `${i * 90}ms` }}
              >
                {/* Step number */}
                <div className="absolute -top-5 left-7 w-10 h-10 rounded-full bg-brand-gold text-brand-maroon-dark flex items-center justify-center font-playfair font-bold text-lg shadow-gold-glow border-2 border-brand-gold-light group-hover:rotate-[360deg] transition-transform duration-700">
                  {i + 1}
                </div>
                <div className="absolute top-3 right-3 opacity-30 group-hover:opacity-70 group-hover:rotate-180 transition-all duration-500">
                  <FlowerMotif className="w-7 h-7 text-brand-gold" />
                </div>
                <div className="relative w-16 h-16 mt-3 mb-5">
                  <div className="absolute inset-0 bg-brand-gold/20 rounded-2xl blur-xl group-hover:bg-brand-gold/40 transition-colors" />
                  <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-brand-gold-light to-brand-gold flex items-center justify-center text-brand-maroon-dark group-hover:scale-110 transition-transform">
                    <Icon className="w-8 h-8" />
                  </div>
                </div>
                <h3 className={`font-playfair font-bold text-2xl text-brand-gold-light mb-3 ${isHi ? 'font-hindi-serif' : ''}`}>
                  {isHi ? p.hi : p.en}
                </h3>
                <p className={`text-white/80 text-base leading-relaxed ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                  {isHi ? p.pHi : p.pEn}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <PremiumButton size="lg" onClick={onOpenForm} variant="gold" className="!text-brand-maroon-dark">
            <Sparkles className="w-4 h-4" />
            <span className={`font-bold ${isHi ? 'font-hindi' : 'font-outfit'}`}>
              {isHi ? 'अभी ऑर्डर करें' : 'Order Now'}
            </span>
            <ArrowRight className="w-4 h-4" />
          </PremiumButton>
        </div>
      </div>
    </section>
  );
};

export default WhoShouldGetSection;
